/**
 * TipTap doc JSON → Markdown.
 *
 * Walks the ProseMirror node tree the editor persists in
 * `NoteSnapshot.doc` and produces a portable Markdown string. Covers the
 * node + mark set used by the notes editor (StarterKit + extension-link):
 *
 *   nodes : doc, paragraph, heading(1-3), bulletList, orderedList,
 *           listItem, blockquote, codeBlock, hardBreak, horizontalRule
 *   marks : bold, italic, strike, code, link
 *
 * Unknown nodes / marks fall through to plain text so unsupported content
 * never disappears entirely.
 */

interface PMMark {
  type: string;
  attrs?: Record<string, unknown>;
}

interface PMNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: PMNode[];
  marks?: PMMark[];
  text?: string;
}

const ORDERED_PREFIX = (n: number) => `${n}. `;
const BULLET_PREFIX = "- ";

export function noteDocToMarkdown(doc: unknown): string {
  if (!doc || typeof doc !== "object") return "";
  const node = doc as PMNode;
  const out: string[] = [];
  for (const child of node.content ?? []) {
    out.push(renderBlock(child, ""));
  }
  return (
    out
      .join("")
      .replace(/\n{3,}/g, "\n\n")
      .trim() + "\n"
  );
}

function renderBlock(node: PMNode, indent: string): string {
  switch (node.type) {
    case "paragraph": {
      const text = renderInlines(node.content ?? []);
      return text.length > 0 ? `${indent}${text}\n\n` : "\n";
    }
    case "heading": {
      const level = Math.min(6, Math.max(1, Number(node.attrs?.level ?? 1)));
      const text = renderInlines(node.content ?? []);
      return `${"#".repeat(level)} ${text}\n\n`;
    }
    case "bulletList":
      return renderList(node, indent, false);
    case "orderedList":
      return renderList(node, indent, true);
    case "blockquote": {
      const inner = (node.content ?? [])
        .map((c) => renderBlock(c, ""))
        .join("")
        .trimEnd();
      const quoted = inner
        .split("\n")
        .map((line) => (line.length > 0 ? `> ${line}` : ">"))
        .join("\n");
      return `${quoted}\n\n`;
    }
    case "codeBlock": {
      const lang = typeof node.attrs?.language === "string" ? node.attrs.language : "";
      const text = (node.content ?? []).map((c) => c.text ?? "").join("");
      return `\`\`\`${lang}\n${text}\n\`\`\`\n\n`;
    }
    case "horizontalRule":
      return `---\n\n`;
    case "hardBreak":
      return "  \n";
    default:
      // Best-effort: render children (or text) and move on.
      if (node.text) return escapeText(node.text);
      if (node.content) {
        return node.content.map((c) => renderBlock(c, indent)).join("");
      }
      return "";
  }
}

function renderList(node: PMNode, indent: string, ordered: boolean): string {
  const items = node.content ?? [];
  const lines: string[] = [];
  items.forEach((item, i) => {
    const marker = ordered ? ORDERED_PREFIX(i + 1) : BULLET_PREFIX;
    const itemIndent = " ".repeat(marker.length);
    const itemContent = (item.content ?? [])
      .map((c) => renderBlock(c, indent + itemIndent))
      .join("")
      .trimEnd();
    // Prefix the first line with the marker, indent subsequent lines.
    const [first = "", ...rest] = itemContent.split("\n");
    lines.push(`${indent}${marker}${first}`);
    for (const r of rest) {
      lines.push(r.length > 0 ? `${indent}${itemIndent}${r}` : "");
    }
  });
  return `${lines.join("\n")}\n\n`;
}

function renderInlines(nodes: PMNode[]): string {
  return nodes
    .map((n) => {
      if (n.type === "hardBreak") return "  \n";
      if (n.type !== "text") return n.text ?? "";
      return applyMarks(escapeText(n.text ?? ""), n.marks ?? []);
    })
    .join("");
}

function applyMarks(text: string, marks: PMMark[]): string {
  let out = text;
  // Apply code first so we don't escape ` inside it again.
  const codeMark = marks.find((m) => m.type === "code");
  if (codeMark) out = `\`${unescapeBackticks(out)}\``;
  for (const mark of marks) {
    if (mark.type === "bold") out = `**${out}**`;
    else if (mark.type === "italic") out = `*${out}*`;
    else if (mark.type === "strike") out = `~~${out}~~`;
    else if (mark.type === "link") {
      const href = typeof mark.attrs?.href === "string" ? mark.attrs.href : "";
      if (href) out = `[${out}](${href})`;
    }
  }
  return out;
}

/** Escape characters that would otherwise change Markdown meaning. */
function escapeText(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/([*_`~[\]])/g, "\\$1");
}

function unescapeBackticks(s: string): string {
  // Inline code can't contain unescaped backticks — strip the escape we
  // would have added in `escapeText`.
  return s.replace(/\\`/g, "`");
}
